import * as Sentry from "@sentry/react";

// utils
import { beforeSendEvent } from "../../react/utils/sentry-sanitization";

// config
import SentryConfig from "../../react/constants/Sentry";

/** The subset of the injected global this bundle reads. login.html renders it. */
type PyEnvironment = {
	release?: string;
	stage?: string;
};

const getPyEnvironment = (): PyEnvironment =>
	(window as Window & { PYENVIRONMENT?: PyEnvironment }).PYENVIRONMENT ?? {};

/**
 * Sets up Sentry for the public (login) bundle.
 *
 * Skipped on a developer machine. Sentry does not drop localhost events on its
 * own, and this bundle has no server-injected stage locally, so without the
 * guard local errors would land in a real environment. The app bundle's
 * SentryErrorWrapper does the same.
 */
export const initSentry = (): void => {
	if (window.location.hostname === "localhost") return;

	Sentry.init({
		dsn: SentryConfig.APIKey,
		// Stage, not hostname: every dev box was its own environment (PY-13367). Read the
		// global directly. Importing react/utils/URLs would pull in utils-isomorphic.
		environment: getPyEnvironment().stage || "unknown",
		release: getPyEnvironment().release || "unknown",

		// Drop errors thrown by third-party scripts we don't ship or control.
		// VisitorAPI (https://cdn.visitorapi.com/visitor-api.js) gets injected into
		// some users' pages externally and throws "Unexpected end of JSON input" on
		// every load, which was consuming the entire Sentry quota within ~1 week each
		// month. denyUrls matches the stack frame's source file URL. See PY-12990.
		denyUrls: [/visitorapi\.com/i],
		integrations: [
			Sentry.browserTracingIntegration(),
			Sentry.rewriteFramesIntegration({
				iteratee: (frame: any) => {
					if (frame.filename) {
						frame.in_cdn = /https?:\/\/cdn\.performyard\.com/.test(frame.filename);
						frame.in_py = /https?:\/\/talent\.performyard\.com/.test(frame.filename);
						frame.in_app = frame.in_cdn | frame.in_py;
					}
					return frame;
				},
			}),
		],

		// Called for message and error events
		beforeSend: beforeSendEvent,
	});
};
